import { MdOutlineKingBed } from "react-icons/md";
import Loader from "../../../components/loader/Loader";
import Carousel from "react-material-ui-carousel";
import { useGetPropertyDetailsWithImagesQuery } from "../../../redux/api/leadsApi";
import { PROPERTY_IMAGES_URL } from "../../../constants/url";
import { LuBath } from "react-icons/lu";
import { Paper } from "@mui/material";
import DescriptionText from "../../../components/Description";
import { useLeadsData } from "../../../hooks/useContext/leadsData";
import { AiOutlineAreaChart } from "react-icons/ai";
import { FaMapMarkerAlt } from "react-icons/fa";

const LeadDetails = () => {
  const currentPath = window.location.pathname.split("/");
  const agentId = Number(currentPath[3]);
  const userId = Number(currentPath[5]);
  const leadId = Number(currentPath[currentPath.length - 1]);

  const { agencyLeads: leadsData } = useLeadsData();

  // ✅ Match lead safely
  const lead = leadsData?.find(
    (item) =>
      Number(item.agentid) === agentId &&
      Number(item.userid) === userId &&
      Number(item.leadid) === leadId
  );

  // ✅ Fetch property details
  const { data, isLoading } = useGetPropertyDetailsWithImagesQuery(
    lead?.propertyid,
    { skip: !lead?.propertyid }
  );

  const property = data?.Properties?.[0];
  const propertyImages = property?.propertyimages ?? [];
  const bedrooms = property?.bedrooms === -1 ? "Studio" : property?.bedrooms;

  return (
    <div className="w-[90%] h-auto m-auto">
      {/* 🏡 Image Carousel */}
      <div className="w-full h-auto overflow-hidden">
        <Carousel>
          {isLoading ? (
            <Loader />
          ) : propertyImages.length > 0 ? (
            propertyImages.map((img, i) => (
              <Item key={i} image={PROPERTY_IMAGES_URL + img} />
            ))
          ) : (
            <div className="text-center py-10 text-gray-500">
              No images available
            </div>
          )}
        </Carousel>
      </div>

      {/* 🏷 Property Info */}
      <div className="w-[90%] m-auto">
        <h3 className="mt-5 font-bold text-xl">
          Price:{" "}
          {property?.propertyprice ? `PKR ${property.propertyprice}` : "N/A"}
        </h3>

        <h3 className="text-green uppercase font-bold my-5 text-xl">
          {property?.propertytitle || "Untitled Property"}
        </h3>

        <div className="text-gray-500 flex items-center mb-3">
          <FaMapMarkerAlt className="mr-2" />
          <span>{property?.propertyaddress}</span>
        </div>

        <div className="sm:flex flex-wrap">
          <Detail
            icon={<AiOutlineAreaChart size={16} />}
            label="Marla"
            value={property?.propertysizemarla}
          />
          <Detail
            icon={<MdOutlineKingBed size={20} />}
            label="Beds"
            value={bedrooms}
          />
          <Detail
            icon={<LuBath size={16} />}
            label="Baths"
            value={property?.bathrooms}
          />
        </div>

        {/* 🏠 Property Details Section */}
        <div className="w-full mt-5">
          <h3 className="font-bold text-lg">Property Details</h3>
          <div className="flex flex-wrap flex-col sm:flex-row sm:justify-between">
            <DetailRow label="Reference No" value={property?.propertyrefnum} />
            <DetailRow
              label="Published On"
              value={property?.publisheddate?.split("T")[0]}
            />
            <DetailRow label="Property Type" value={property?.propertytype1} />
            <DetailRow label="Purpose" value={property?.propertypurpose1} />
            <DetailRow label="City" value={property?.cityname} />
            <DetailRow label="Sub Location" value={property?.sublocation} />
            <DetailRow label="Province" value={property?.provname} />
            <DetailRow label="Country" value={property?.countryname} />
            <DetailRow
              label="Size (sqft)"
              value={
                property?.propertysizesqft
                  ? Number(property.propertysizesqft).toFixed(1)
                  : ""
              }
            />
            <DetailRow
              label="Google Map"
              value={
                property?.coordinates ? (
                  <a
                    href={property.coordinates}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    View on Map
                  </a>
                ) : (
                  "N/A"
                )
              }
            />
          </div>
        </div>

        {/* 🏢 Agency / Agent Details */}
        <div className="w-full mt-5">
          <h3 className="font-bold text-lg mb-3">Agent & Agency</h3>
          <div className="flex flex-wrap justify-between">
            <DetailRow label="Agent Name" value={property?.agentname} />
            <DetailRow label="Agent Email" value={property?.agentemail} />
            <DetailRow label="Agent Phone" value={property?.agentphone} />
            <DetailRow label="Agency Name" value={property?.agencyname} />
            <DetailRow label="Agency Email" value={property?.agencyemail} />
            <DetailRow label="Agency Phone" value={property?.agencyphone} />
          </div>
        </div>

        {/* 🧩 Amenities */}
        {property?.Amenities && property.Amenities.length > 0 && (
          <div className="w-full mt-5">
            <h3 className="font-bold text-lg mb-3">Amenities</h3>
            <ul className="list-disc list-inside grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 text-gray-700">
              {property.Amenities.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 📝 Description */}
        <h3 className="font-bold my-5 text-lg">Description</h3>
        <DescriptionText
          text={property?.propertydescription || "No description available."}
        />
      </div>
    </div>
  );
};

export default LeadDetails;

function Item({ image }) {
  return (
    <Paper>
      <img
        src={image}
        alt="Property"
        className="w-full h-[40vh] sm:h-[60vh] rounded-lg object-cover"
      />
    </Paper>
  );
}

function Detail({ icon, label, value }) {
  return (
    <p className="sm:mx-2.5 flex justify-between items-center w-full sm:w-auto">
      <span className="m-1 sm:m-2.5 flex items-center">
        {icon}
        <span className="ml-1">{label}:</span>
      </span>
      <span>{value || "N/A"}</span>
    </p>
  );
}

function DetailRow({ label, value }) {
  return (
    <p className="w-[100%] sm:w-[45%] my-2 flex justify-between text-gray-700">
      <span className="font-medium">{label}</span>
      <span>{value || "N/A"}</span>
    </p>
  );
}
